import { inject, Injectable } from '@angular/core';
import { appSetting } from '@core/constants';
import { Credentials } from '@core/entities';
// The below paths are relative because when injected in StandAlone component it causes circular dependency somehow, and this is the only way to fix it
import { CredentialsService } from './credentials.service';
import { PERMISSIONS } from '../../auth/enums/permissions.enum';
import { ROLE } from '../../auth/enums/roles.enum';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly _credentialsService = inject(CredentialsService);

  private readonly _credentials: Credentials;

  constructor() {
    this._credentials = this._credentialsService.credentials;
    if (!this._credentials) {
      this._credentials = this._credentialsService.credentials;
    }
  }

  get userRole(): ROLE {
    return this._credentials?.roles[0] as ROLE;
  }

  isAdmin(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.roles.includes(ROLE.ADMIN) && this._credentials.isAdmin;
  }

  isEditor(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.roles.includes(ROLE.EDITOR) && this._credentials.isEditor;
  }

  isFunctionary(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.isFunctionary && this._credentials.roles.includes(ROLE.FUNCTIONARY);
  }

  isSecretary(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.isSecretary || this._credentials.roles.includes(ROLE.SECRETARY);
  }

  isMember(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.isMember || this._credentials.roles.includes(ROLE.MEMBER);
  }

  isMemberLight(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.isMemberLight || this._credentials.roles.includes(ROLE.MEMBER_LIGHT);
  }

  isMemberLightAdmin(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.isMemberLightAdmin || this._credentials.roles.includes(ROLE.MEMBER_LIGHT_ADMIN);
  }

  isGuest(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.roles.includes(ROLE.GUEST) && this._credentials.guestUser;
  }

  isUser(): boolean {
    if (!this._credentials) {
      return false;
    }
    return this._credentials.roles.includes(ROLE.USER);
  }

  isCourseAccessAllowed(): boolean {
    if (!this._credentials) {
      return false;
    }

    if (!this._credentials.coursePermission || this._credentials.coursePermission.length === 0) {
      return false;
    }

    return this._credentials.coursePermission[0]?.vCloudCourseAccess && this._credentials.coursePermission[0]?.vCloudCourseAccess === 1;
  }

  isWorkShiftsAccessAllowed(): boolean {
    if (!this._credentials) {
      return false;
    }

    if (!this._credentials.coursePermission || this._credentials.coursePermission.length === 0) {
      return false;
    }

    return this._credentials.coursePermission[0]?.vCloudWorkShiftsAccess && this._credentials.coursePermission[0]?.vCloudWorkShiftsAccess === 1;
  }

  hasRole(requiredRoles: ROLE[]): boolean {
    const credentials = this._credentialsService.credentials;
    if (!credentials || !credentials.roles) {
      return false;
    }
    // Check if any of the user's roles match the required roles
    return requiredRoles.some((role) => credentials.roles.includes(role));
  }

  hasPermission(permission: PERMISSIONS): boolean {
    const credentials = this._credentialsService.credentials;
    if (!credentials) {
      return false;
    }

    // Extracting roles from the credentials
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { roles, isAdmin, isEditor, isSecretary, isMember, isMemberLight, isMemberLightAdmin, isFunctionary } = credentials;

    // First, check the specific role-based permissions
    for (const role of roles) {
      const rolePermissions = appSetting.role[role as ROLE]; // Cast to ROLE ENUM
      if (rolePermissions && this._checkPermission(rolePermissions, permission)) {
        return true;
      }
    }

    // Then, check other boolean properties
    // if (isAdmin || isEditor || isSecretary || isMember || isMemberLight || isMemberLightAdmin || isFunctionary) {
    //   return true;
    // }

    return false;
  }

  private _checkPermission(rolePermissions: any, permission: PERMISSIONS): boolean {
    const keys = permission.split('.');
    let currentLevel = rolePermissions;

    for (const key of keys) {
      if (!currentLevel[key]) {
        return false;
      }
      currentLevel = currentLevel[key];
    }

    return currentLevel === 'Yes';
  }
}
